import {
  ArrowRight,
  Bot,
  Calendar,
  CheckCircle2,
  FileText,
  Inbox,
  MessageSquare,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Star,
  TrendingUp,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { CmsLink } from '@saas-bot/types';

import { Reveal } from '../motion/Reveal';

/** Named icons authors can reference from the CMS. Falls back to a spark. */
const ICONS: Record<string, LucideIcon> = {
  inbox: Inbox,
  bot: Bot,
  'trending-up': TrendingUp,
  calendar: Calendar,
  'file-text': FileText,
  'shield-check': ShieldCheck,
  'shopping-bag': ShoppingBag,
  'message-square': MessageSquare,
  zap: Zap,
  star: Star,
  check: CheckCircle2,
  sparkles: Sparkles,
};

export function BlockIcon({ name, className }: { name: string; className?: string }): JSX.Element {
  const Icon = ICONS[name] ?? Sparkles;
  return <Icon className={className} strokeWidth={1.75} aria-hidden />;
}

export function Container({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <div className={`mx-auto w-full max-w-6xl px-5 sm:px-8 ${className}`}>{children}</div>;
}

export function Eyebrow({ children }: { children: ReactNode }): JSX.Element | null {
  if (!children) return null;
  return (
    <span className="accent-text inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em]">
      <span className="accent-bg h-1.5 w-1.5 rounded-full" aria-hidden />
      {children}
    </span>
  );
}

/** Internal routes use the router; external/mailto/anchors use a plain anchor. */
function isInternal(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//');
}

export function SmartLink({
  href,
  className,
  children,
}: {
  href: string;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  if (isInternal(href)) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a
      href={href}
      className={className}
      target={href.startsWith('http') ? '_blank' : undefined}
      rel="noreferrer"
    >
      {children}
    </a>
  );
}

type ButtonVariant = 'primary' | 'secondary' | 'ghost';

const BUTTON_BASE =
  'group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 accent-ring disabled:opacity-50';

export function CtaButton({
  link,
  variant = 'primary',
  withArrow = false,
}: {
  link: CmsLink;
  variant?: ButtonVariant;
  withArrow?: boolean;
}): JSX.Element | null {
  if (!link?.label) return null;

  const styles: Record<ButtonVariant, string> = {
    primary:
      'bg-[linear-gradient(120deg,var(--mk-blue),var(--mk-violet))] text-white shadow-[0_14px_40px_-16px_rgba(120,90,255,0.9)] hover:brightness-115 hover:-translate-y-0.5',
    secondary:
      'border border-[var(--mk-line-strong)] bg-[var(--color-surface)] text-[var(--color-ink)] hover:border-[var(--mk-cyan)] hover:-translate-y-0.5',
    ghost: 'accent-text hover:opacity-80',
  };

  return (
    <SmartLink href={link.href} className={`${BUTTON_BASE} ${styles[variant]}`}>
      {link.label}
      {withArrow && (
        <ArrowRight
          className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      )}
    </SmartLink>
  );
}

export function SectionHeading({
  eyebrow,
  heading,
  subheading,
  align = 'center',
}: {
  eyebrow?: string;
  heading: string;
  subheading?: string;
  align?: 'center' | 'left';
}): JSX.Element {
  const alignment =
    align === 'center'
      ? 'mx-auto max-w-2xl text-center items-center'
      : 'max-w-2xl text-left items-start';
  return (
    <Reveal className={`flex flex-col gap-4 ${alignment}`}>
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <h2 className="font-[var(--font-display)] text-3xl font-semibold leading-tight text-[var(--color-ink)] sm:text-4xl">
        {heading}
      </h2>
      {subheading ? (
        <p className="text-lg leading-relaxed text-[var(--color-ink-soft)]">{subheading}</p>
      ) : null}
    </Reveal>
  );
}
